import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, MapPin, Hammer, Phone, Award, Send, Star } from 'lucide-react';
import { companyInfo } from '../data';
import ServiceCard from '../components/ServiceCard';
import ReferenceCard from '../components/ReferenceCard';
import useDocumentTitle from '../hooks/useDocumentTitle';

export default function Home() {
  useDocumentTitle();
  const [services, setServices] = useState([]);
  const [references, setReferences] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [loadingReferences, setLoadingReferences] = useState(true);

  // Hero contact form state
  const [heroFormData, setHeroFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    message: '',
    website_url: '' // Honeypot field
  });
  const [heroFormStatus, setHeroFormStatus] = useState({ type: '', message: '' });
  const [heroSubmitting, setHeroSubmitting] = useState(false);

  const handleHeroInputChange = (e) => {
    const { name, value } = e.target;
    setHeroFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleHeroSubmit = async (e) => {
    e.preventDefault();
    setHeroSubmitting(true);
    setHeroFormStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(heroFormData),
      });

      if (response.ok) {
        setHeroFormStatus({
          type: 'success',
          message: 'Tak! Jeg har modtaget din henvendelse og vender tilbage hurtigst muligt.',
        });
        setHeroFormData({ name: '', phone: '', email: '', address: '', message: '', website_url: '' });
      } else {
        throw new Error('Fejl ved indsendelse');
      }
    } catch (error) {
      setHeroFormStatus({
        type: 'error',
        message: 'Der skete en fejl. Ring venligst direkte på ' + companyInfo.phone,
      });
    } finally {
      setHeroSubmitting(false);
    }
  };

  useEffect(() => {
    // Fetch services
    fetch('https://api.dev.auxo.dk/public/content/ydelser')
      .then((res) => res.json())
      .then((data) => {
        setServices(data.slice(0, 3));
        setLoadingServices(false);
      })
      .catch((err) => {
        console.error('Error fetching services:', err);
        setLoadingServices(false);
      });

    // Fetch references
    fetch('https://api.dev.auxo.dk/public/content/referencer')
      .then((res) => res.json())
      .then((data) => {
        setReferences(data);
        setLoadingReferences(false);
      })
      .catch((err) => {
        console.error('Error fetching references:', err);
        setLoadingReferences(false);
      });
  }, []);

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-neutral-950 text-white py-10 md:py-20 lg:py-28 xl:py-36 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/58aac8da-8467-43e1-8dec-94cd5cd444a3/66b10b6d-b2c2-4eb7-8ae5-8e9287047079.jpg"
            alt="Klassisk dansk murværk"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-950/0 via-neutral-950/70 to-neutral-950" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Text content */}
            <div className="lg:col-span-7 flex flex-col items-start">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight mb-6">
                Klassisk murerarbejde <br />
                <span className="text-red-600">udført med stolthed</span>
              </h1>
              <p className="text-neutral-300 text-base md:text-lg leading-relaxed mb-8 max-w-xl">
                Jeg leverer solidt facade- og murværk i Langå, Randers og omegn. Høj kvalitet, ordentlig oprydning og aftaler, der holder.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mt-2">
                <a
                  href="#kontakt-form-hero"
                  className="bg-red-700 hover:bg-red-800 text-white text-sm font-bold uppercase tracking-wider px-8 py-4 rounded-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg text-center shadow-md flex items-center justify-center gap-2"
                >
                  <span>Kontakt mig</span>
                  <ArrowRight size={16} />
                </a>
                <a
                  href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`}
                  className="bg-white hover:bg-neutral-100 text-neutral-950 text-sm font-bold uppercase tracking-wider px-8 py-4 rounded-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg flex items-center justify-center gap-2 text-center"
                >
                  <Phone size={16} className="text-red-600 fill-red-600 stroke-red-600" />
                  <span>{companyInfo.phone}</span>
                </a>
              </div>
            </div>

            {/* Right Column: Contact Form Card */}
            <div id="kontakt-form-hero" className="lg:col-span-5 w-full bg-white text-neutral-900 p-6 md:p-8 rounded-sm shadow-2xl border border-neutral-200/10">
              <h3 className="text-lg font-extrabold text-neutral-950 tracking-tight mb-1">
                Få et uforpligtende tilbud
              </h3>
              <p className="text-neutral-600 text-xs mb-6">
                Udfyld formularen, så vender jeg hurtigt tilbage med et uforpligtende tilbud på din mureropgave.
              </p>

              <form onSubmit={handleHeroSubmit} className="space-y-4">
                {/* Honeypot field - absolutely hidden from humans */}
                <div className="sr-only absolute -top-[9999px] -left-[9999px]">
                  <label htmlFor="hero_website_url">Do not fill this out</label>
                  <input
                    type="text"
                    id="hero_website_url"
                    name="website_url"
                    value={heroFormData.website_url}
                    onChange={handleHeroInputChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-700 mb-1">
                    Navn *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={heroFormData.name}
                    onChange={handleHeroInputChange}
                    className="w-full bg-neutral-50 border border-neutral-300 rounded-sm px-3 py-2 text-sm text-neutral-900 focus:outline-none focus:border-red-700"
                    placeholder="Dit fulde navn"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-700 mb-1">
                      Telefonnummer *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={heroFormData.phone}
                      onChange={handleHeroInputChange}
                      className="w-full bg-neutral-50 border border-neutral-300 rounded-sm px-3 py-2 text-sm text-neutral-900 focus:outline-none focus:border-red-700"
                      placeholder="Tlf. nr."
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-700 mb-1">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={heroFormData.email}
                      onChange={handleHeroInputChange}
                      className="w-full bg-neutral-50 border border-neutral-300 rounded-sm px-3 py-2 text-sm text-neutral-900 focus:outline-none focus:border-red-700"
                      placeholder="din@email.dk"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-700 mb-1">
                    Adresse (hvor opgaven skal udføres)
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={heroFormData.address}
                    onChange={handleHeroInputChange}
                    className="w-full bg-neutral-50 border border-neutral-300 rounded-sm px-3 py-2 text-sm text-neutral-900 focus:outline-none focus:border-red-700"
                    placeholder="F.eks. Gadenavn 12, 8870 Langå"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-700 mb-1">
                    Beskrivelse af mureropgaven *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows="3"
                    value={heroFormData.message}
                    onChange={handleHeroInputChange}
                    className="w-full bg-neutral-50 border border-neutral-300 rounded-sm px-3 py-2 text-sm text-neutral-900 focus:outline-none focus:border-red-700 resize-none"
                    placeholder="Beskriv mureropgaven her..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={heroSubmitting}
                  className="w-full bg-red-700 hover:bg-red-800 disabled:bg-neutral-400 text-white text-xs font-bold uppercase tracking-wider py-3.5 rounded-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg shadow-md flex items-center justify-center gap-2 cursor-pointer mt-2"
                >
                  <Send size={12} />
                  <span>{heroSubmitting ? 'Sender...' : 'Send forespørgsel'}</span>
                </button>

                {heroFormStatus.message && (
                  <div
                    className={`p-3 rounded-sm text-xs font-medium mt-3 ${
                      heroFormStatus.type === 'success'
                        ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                        : 'bg-red-50 border border-red-200 text-red-800'
                    }`}
                  >
                    {heroFormStatus.message}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Trust / Values Bar */}
      <section className="bg-white border-b border-neutral-200 py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {companyInfo.values.map((val, idx) => (
              <div key={idx} className="flex flex-col items-center text-center gap-3">
                <div className="text-neutral-950 flex-shrink-0">
                  {idx === 0 ? <Hammer size={40} /> : idx === 1 ? <ShieldCheck size={40} /> : <Award size={40} />}
                </div>
                <div>
                  <h3 className="text-base font-bold text-neutral-950 mb-1">{val.title}</h3>
                  <p className="text-neutral-600 text-sm leading-relaxed">{val.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About / Profile Intro Section */}
      <section className="bg-neutral-50 overflow-hidden relative border-b border-neutral-200">
        <div className="flex flex-col lg:flex-row items-stretch min-h-[500px]">
          {/* Image - No padding around it */}
          <div className="w-full lg:w-1/2 relative min-h-[350px] lg:min-h-auto">
            <img
              src="https://assets.auxodev.dk/assets/58aac8da-8467-43e1-8dec-94cd5cd444a3/97112bd3-ac1c-42aa-a673-1cd5affe67e5.jpg"
              alt="Flot murerarbejde udført af Pedersen Byg"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>

          {/* Text - With watermark in the right side */}
          <div className="w-full lg:w-1/2 py-16 md:py-24 px-6 md:px-12 lg:px-16 flex flex-col justify-center relative">
            {/* Watermark of a few bricks in the bottom right corner */}
            <div className="absolute bottom-0 right-0 pointer-events-none select-none z-0 opacity-[0.12] w-48 h-36">
              <svg viewBox="0 0 180 100" className="text-red-700 w-full h-full" xmlns="http://www.w3.org/2000/svg">
                {/* Row 1 (top) */}
                <rect x="70" y="10" width="80" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" rx="2" />
                
                {/* Row 2 (middle) */}
                <rect x="30" y="38" width="80" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" rx="2" />
                <rect x="114" y="38" width="60" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" rx="2" />
                
                {/* Row 3 (bottom) */}
                <rect x="10" y="66" width="80" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" rx="2" />
                <rect x="94" y="66" width="80" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" rx="2" />
              </svg>
            </div>

            <div className="relative z-10 flex flex-col gap-6">
              <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-950 tracking-tight">
                Troværdigt murerarbejde med rødder i Langå
              </h2>
              <p className="text-neutral-700 leading-relaxed">
                Jeg hedder Christian Juhl Pedersen, og jeg er murermesteren bag Pedersen Byg. Med 6 års erfaring i murerfaget har jeg opbygget en solid forretning, der bygger på de klassiske murerdyder: Præcision, stolthed og ærlighed.
              </p>
              <p className="text-neutral-700 leading-relaxed">
                Jeg udfører alt inden for facade- og murværk, omfugning, renovering og tilbygninger. Uanset om det er en lille reparation eller et større byggeri, går jeg til opgaven med samme professionalisme og omhu.
              </p>
              <div className="flex flex-wrap gap-6 items-center mt-4 pt-4 border-t border-neutral-200">
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider">Ejer & Murermester</p>
                  <p className="text-base font-bold text-neutral-950">{companyInfo.owner}</p>
                </div>
                <div className="h-8 w-px bg-neutral-200 hidden sm:block" />
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider">Ring direkte</p>
                  <a href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`} className="text-base font-bold text-red-700 hover:text-red-800 transition-colors">
                    {companyInfo.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-white py-12 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-950 tracking-tight mt-2 mb-3">
              Mine Murerkompetencer
            </h2>
            <p className="text-neutral-600 text-sm md:text-base">
              Jeg dækker alle aspekter af murerfaget, men har særlig stor ekspertise inden for facade- og murværk samt renovering.
            </p>
          </div>

          {loadingServices ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((n) => (
                <div key={n} className="animate-pulse bg-neutral-100 h-96 rounded-sm" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          )}

          <div className="text-center mt-8">
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-2 bg-neutral-950 hover:bg-neutral-900 text-white text-xs font-bold uppercase tracking-wider px-6 py-3.5 rounded-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md shadow-sm"
            >
              <span>Se alle mine ydelser</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-red-700 text-white py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10" />
        
        {/* Left Brick Watermark */}
        <div className="absolute -left-12 -bottom-12 pointer-events-none select-none z-0 opacity-[0.07] w-80 h-56 rotate-12 hidden md:block">
          <svg viewBox="0 0 200 120" className="text-white w-full h-full" xmlns="http://www.w3.org/2000/svg">
            {/* Row 1 */}
            <rect x="0" y="0" width="95" height="35" fill="none" stroke="currentColor" strokeWidth="2" rx="1" />
            <rect x="100" y="0" width="95" height="35" fill="none" stroke="currentColor" strokeWidth="2" rx="1" />
            {/* Row 2 */}
            <rect x="-50" y="40" width="95" height="35" fill="none" stroke="currentColor" strokeWidth="2" rx="1" />
            <rect x="50" y="40" width="95" height="35" fill="none" stroke="currentColor" strokeWidth="2" rx="1" />
            <rect x="150" y="40" width="95" height="35" fill="none" stroke="currentColor" strokeWidth="2" rx="1" />
            {/* Row 3 */}
            <rect x="0" y="80" width="95" height="35" fill="none" stroke="currentColor" strokeWidth="2" rx="1" />
            <rect x="100" y="80" width="95" height="35" fill="none" stroke="currentColor" strokeWidth="2" rx="1" />
          </svg>
        </div>

        {/* Right Brick Watermark */}
        <div className="absolute -right-12 -top-12 pointer-events-none select-none z-0 opacity-[0.07] w-80 h-56 -rotate-12 hidden md:block">
          <svg viewBox="0 0 200 120" className="text-white w-full h-full" xmlns="http://www.w3.org/2000/svg">
            {/* Row 1 */}
            <rect x="0" y="0" width="95" height="35" fill="none" stroke="currentColor" strokeWidth="2" rx="1" />
            <rect x="100" y="0" width="95" height="35" fill="none" stroke="currentColor" strokeWidth="2" rx="1" />
            {/* Row 2 */}
            <rect x="-50" y="40" width="95" height="35" fill="none" stroke="currentColor" strokeWidth="2" rx="1" />
            <rect x="50" y="40" width="95" height="35" fill="none" stroke="currentColor" strokeWidth="2" rx="1" />
            <rect x="150" y="40" width="95" height="35" fill="none" stroke="currentColor" strokeWidth="2" rx="1" />
            {/* Row 3 */}
            <rect x="0" y="80" width="95" height="35" fill="none" stroke="currentColor" strokeWidth="2" rx="1" />
            <rect x="100" y="80" width="95" height="35" fill="none" stroke="currentColor" strokeWidth="2" rx="1" />
          </svg>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-6 text-center flex flex-col items-center gap-6">
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
            Skal jeg kigge på dit murværk?
          </h2>
          <p className="text-red-100 text-lg max-w-2xl">
            Uanset om du skal have omfuget facaden, renoveret skorstenen eller bygget en ny læmur, står jeg klar med professionel rådgivning og et uforpligtende tilbud.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-4">
            <Link
              to="/kontakt"
              className="bg-white hover:bg-neutral-100 text-red-700 text-sm font-bold uppercase tracking-wider px-8 py-4 rounded-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg shadow-md"
            >
              Kontakt mig nu
            </Link>
            <a
              href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`}
              className="border border-white hover:bg-white/10 text-white text-sm font-bold uppercase tracking-wider px-8 py-4 rounded-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg flex items-center justify-center gap-2"
            >
              <Phone size={16} />
              <span>Ring {companyInfo.phone}</span>
            </a>
          </div>
        </div>
      </section>

      {/* References Section */}
      <section className="bg-neutral-100 text-neutral-900 py-20 md:py-24 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-4">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-950 tracking-tight">
                Udvalgte Referencer
              </h2>
            </div>
            <p className="text-neutral-600 max-w-md">
              Jeg er stolt af det arbejde, jeg leverer. Her er et udvalg af mine seneste projekter udført for glade kunder i lokalområdet.
            </p>
          </div>

          {loadingReferences ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((n) => (
                <div key={n} className="animate-pulse bg-white border border-neutral-200/80 rounded-sm shadow-md h-96" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {references.map((ref) => (
                <ReferenceCard key={ref.id} reference={ref} />
              ))}
            </div>
          )}
        </div>
      </section>

    </div>
  );
}
